// src/features/devices/history/useUndoRedoHotkeys.ts
import { useEffect } from "react";
import { thunkUndo } from "./thunkUndo"; // juster path
import { thunkRedo } from "./thunkRedo"; // juster path
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { selectCanUndo, selectCanRedo } from "./deviceHistorySlice";

const isEditableElement = (el: EventTarget | null): boolean => {
  if (!el || !(el instanceof HTMLElement)) return false;

  const tag = el.tagName.toLowerCase();
  if (tag === "input" || tag === "textarea" || tag === "select") return true;

  // contenteditable (inkl div med editor)
  if (el.isContentEditable) return true;

  // noen komponenter bruker "role=textbox"
  const role = el.getAttribute("role");
  if (role === "textbox") return true;

  return false;
};

export const useUndoRedoHotkeys = (): void => {
  const dispatch = useAppDispatch();
  const canUndo = useAppSelector(selectCanUndo);
  const canRedo = useAppSelector(selectCanRedo);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      // Ikke stjel hotkeys mens brukeren skriver i et felt
      if (isEditableElement(e.target)) return;

      const isMac = navigator.platform.toLowerCase().includes("mac");

      const ctrlOrCmd = isMac ? e.metaKey : e.ctrlKey;

      // Undo: Ctrl/Cmd + Z (uten shift)
      if (ctrlOrCmd && !e.shiftKey && (e.key === "z" || e.key === "Z")) {
        if (!canUndo) return;
        e.preventDefault();
        dispatch(thunkUndo());
        return;
      }

      // Redo:
      // - Windows/Linux: Ctrl + Y
      // - Mac: Cmd + Shift + Z
      if (isMac) {
        if (ctrlOrCmd && e.shiftKey && (e.key === "z" || e.key === "Z")) {
          if (!canRedo) return;
          e.preventDefault();
          dispatch(thunkRedo());
          return;
        }
      } else {
        if (ctrlOrCmd && (e.key === "y" || e.key === "Y")) {
          if (!canRedo) return;
          e.preventDefault();
          dispatch(thunkRedo());
          return;
        }
      }
    };

    window.addEventListener("keydown", onKeyDown, { capture: true });
    return () => window.removeEventListener("keydown", onKeyDown, { capture: true } as any);
  }, [dispatch, canUndo, canRedo]);
};
